import { Slide } from "@jelly/lesson";
import variables from "../../../../../../assets/styles/sass-resources.scss";
import config from "appConfig";

const principle: Slide = {
  title: "Intensity",
  layout: {
    type: "customCase010202",
    options: {
      practical: {
        list: [
          `When
          <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <mi>k</mi>
            <mi>d</mi>
            <mo>≪</mo>
            <mn>1</mn>
          </math>,
          the phase shift between both microphones is very small and
          <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <mi>I</mi>
            <mi>m</mi>
            <mo>(</mo>
              <msub>
                <mi>p</mi>
                <mn>1</mn>
              </msub>
              <msubsup>
                <mi>p</mi>
                <mn>2</mn>
                <mo>*</mo>
              </msubsup>
            <mo>)</mo>
            <mo>≪</mo>
            <mi>R</mi>
            <mi>e</mi>
            <mo>(</mo>
              <msub>
                <mi>p</mi>
                <mn>1</mn>
              </msub>
              <msubsup>
                <mi>p</mi>
                <mn>2</mn>
                <mo>*</mo>
              </msubsup>
            <mo>)</mo>
          </math>`,
          `In practice the phase shift becomes lower than the phase error.`,
          `When
          <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <mi>k</mi>
            <mi>d</mi>
          </math>
          is too large, the approximation of the gradient is no longer valid.<br>
          Hence, <span style="color:${variables["primary"]}">in practice, the intensimetry is efficient for</span>
          <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <mn>0.05</mn>
            <mo><</mo>
            <mi>k</mi>
            <mi>d</mi>
            <mo><</mo>
            <mn>1.2</mn>
          </math>`,
          `The previous practical range leads, with d = 1.2 cm, to a frequency range from 200 Hz to 4.5 kHz for a 1dB accuracy,<br />and up to 8 kHz for a 3dB accuracy (
          <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <mi>s</mi>
            <mi>i</mi>
            <mi>n</mi>
            <mi>c</mi>
            <mo>(</mo>
            <mi>k</mi>
            <mi>d</mi>
            <mo>)</mo>
            <mo>=</mo>
            <mn>0.5</mn>
          </math>
          ).`,
        ],
        images: [
          `${config.ASSETS_URL}/01/case-02/slide02-image02.svg`,
          `${config.ASSETS_URL}/01/case-02/slide02-image03.svg`,
          `${config.ASSETS_URL}/01/case-02/slide02-image04.svg`,
        ],
      },
      theory: {
        list: [
          `In the frequency domain, it has been seen that the intensity is given by
          <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <mi mathvariant=bold>I</mi>
            <mo>=</mo>
            <mfrac>
                <mn scriptlevel=0>1</mn>
                <mn scriptlevel=0>4</mn>
            </mfrac>
            <mi>p</mi>
            <msup>
                <mi mathvariant=bold>v</mi>
                <mo>*</mo>
            </msup>
            <mo>+</mo>
            <msup>
                <mi>p</mi>
                <mo>*</mo>
            </msup>
            <mi mathvariant=bold>v</mi>
          </math>
          (see Lesson 1.9 for details)`,
          `Euler equation relates the velocity to the pressure by
          <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <mi>j</mi>
            <mi>ω</mi>
            <msub>
              <mi>p</mi>
              <mn>0</mn>
            </msub>
            <mi mathvariant=bold>v</mi>
            <mo>=</mo>
            <mo>-</mo>
            <mtext mathvariant=bold>
              grad
            </mtext>
            <mo>(</mo>
            <mi>p</mi>
            <mo>)</mo>
          </math>
          (see Lesson 1.10 for details)`,
          `Consider an intensimetric probe made with two microphones along r at a distance d one from each other (see figure).<br />
          The pressure gradient along r can be approximated giving the projection of the velocity on r by
          <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <mi>j</mi>
            <mi>ω</mi>
            <msub>
              <mi>p</mi>
              <mn>0</mn>
            </msub>
            <msub>
              <mi>v</mi>
              <mi>r</mi>
            </msub>
            <mo>≈</mo>
            <mfrac>
                <mrow>
                <msub>
                  <mi scriptlevel=0>p</mi>
                  <mn scriptlevel="+1">1</mn>
                </msub>
                <mo>-</mo>
                <msub>
                  <mi scriptlevel=0>p</mi>
                  <mn scriptlevel="+1">2</mn>
                </msub>
                </mrow>
                <mrow>
                  <mi scriptlevel=0>d</mi>
                </mrow>
            </mfrac>
          </math>
          <br />
          The pressure can be approximated by
          <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <mi>p</mi>
            <mo>≈</mo>
            <mfrac>
                <mrow>
                <msub>
                  <mi scriptlevel=0>p</mi>
                  <mn scriptlevel="+1">1</mn>
                </msub>
                <mo>-</mo>
                <msub>
                  <mi scriptlevel=0>p</mi>
                  <mn scriptlevel="+1">2</mn>
                </msub>
                </mrow>
                <mrow>
                  <mn scriptlevel=0>2</mn>
                </mrow>
            </mfrac>
          </math>`,
          `Using these approximations, the intensity along r is then given by
          <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <msub>
              <mi>I</mi>
              <mi>r</mi>
            </msub>
            <mo>≈</mo>
            <mfrac>
                <mrow>
                  <mo>(</mo>
                  <msub>
                    <mi scriptlevel=0>p</mi>
                    <mn scriptlevel="+1">1</mn>
                  </msub>
                  <mo>+</mo>
                  <msub>
                    <mi scriptlevel=0>p</mi>
                    <mn scriptlevel="+1">2</mn>
                  </msub>
                  <mo>)</mo>
                  <mo>(</mo>
                  <msub>
                    <mi scriptlevel=0>p</mi>
                    <mn scriptlevel="+1">1</mn>
                  </msub>
                  <mo>-</mo>
                  <msub>
                    <mi scriptlevel=0>p</mi>
                    <mn scriptlevel="+1">2</mn>
                  </msub>
                  <msup>
                    <mo>)</mo>
                    <mo>*</mo>
                  </msup>
                  <mo>-</mo>
                  <mo>(</mo>
                  <msub>
                    <mi scriptlevel=0>p</mi>
                    <mn scriptlevel="+1">1</mn>
                  </msub>
                  <mo>+</mo>
                  <msub>
                    <mi scriptlevel=0>p</mi>
                    <mn scriptlevel="+1">2</mn>
                  </msub>
                  <msup>
                    <mo>)</mo>
                    <mo>*</mo>
                  </msup>
                  <mo>(</mo>
                  <msub>
                    <mi scriptlevel=0>p</mi>
                    <mn scriptlevel="+1">1</mn>
                  </msub>
                  <mo>-</mo>
                  <msub>
                    <mi scriptlevel=0>p</mi>
                    <mn scriptlevel="+1">2</mn>
                  </msub>
                  <mo>)</mo>
                </mrow>
                <mrow>
                  <mn scriptlevel=0>8</mn>
                  <mi scriptlevel=0>j</mi>
                  <mi scriptlevel=0>ω</mi>
                  <msub>
                    <mi scriptlevel=0>p</mi>
                    <mn scriptlevel="+1">0</mn>
                  </msub>
                  <mi scriptlevel=0>d</mi>
                </mrow>
            </mfrac>
            <mo>=</mo>
            <mfrac>
              <mrow>
                <mi scriptlevel=0>I</mi>
                <mi scriptlevel=0>m</mi>
                <mo>(</mo>
                  <msub>
                    <mi scriptlevel=0>p</mi>
                    <mn scriptlevel="+1">1</mn>
                  </msub>
                  <msubsup>
                    <mi scriptlevel=0>p</mi>
                    <mn scriptlevel="+1">2</mn>
                    <mo scriptlevel="+1">*</mo>
                  </msubsup>
                <mo>)</mo>
              </mrow>
              <mrow>
                <mn scriptlevel=0>2</mn>
                <msub>
                  <mi scriptlevel=0>p</mi>
                  <mn scriptlevel="+1">0</mn>
                </msub>
                <mi scriptlevel=0>c</mi>
                <mi scriptlevel=0>k</mi>
                <mi scriptlevel=0>d</mi>
              </mrow>
            </mfrac>
          </math>
          <br />
          The intensity is hence proportional to the imaginary part of the interspectrum
          <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <msub>
              <mi>p</mi>
              <mn>1</mn>
            </msub>
            <msubsup>
              <mi>p</mi>
              <mn>2</mn>
              <mo>*</mo>
            </msubsup>
          </math>
          <br>
          This result is theoretically valid when
          <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <mi>k</mi>
            <mi>d</mi>
            <mo>≪</mo>
            <mn>1</mn>
          </math>`,
          `A better approximation, equivalent to the previous one when
          <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <mi>k</mi>
            <mi>d</mi>
            <mo>≪</mo>
            <mn>1</mn>
          </math>
          , based on a locally plane wave with an incident <br />angle <b>α</b> (see figure) is given by
          <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <msub>
              <mi >I</mi>
              <mi>r</mi>
            </msub>
            <mo>≈</mo>
            <mfrac>
              <mrow>
                <mi scriptlevel=0>I</mi>
                <mi scriptlevel=0>m</mi>
                <mo>(</mo>
                  <msub>
                    <mi scriptlevel=0>p</mi>
                    <mn scriptlevel="+1">1</mn>
                  </msub>
                  <msubsup>
                    <mi scriptlevel=0>p</mi>
                    <mn scriptlevel="+1">2</mn>
                    <mo scriptlevel="+1">*</mo>
                  </msubsup>
                <mo>)</mo>
              </mrow>
              <mrow>
                <mn scriptlevel=0>2</mn>
                <msub>
                  <mi scriptlevel=0>p</mi>
                  <mn scriptlevel="+1">0</mn>
                </msub>
                <mi scriptlevel=0>c</mi>
                <mi scriptlevel=0>k</mi>
                <mi scriptlevel=0>d</mi>
              </mrow>
            </mfrac>
            <mfrac>
              <mrow>
                <mn scriptlevel=0>1</mn>
              </mrow>
              <mrow>
                <mi scriptlevel=0>s</mi>
                <mi scriptlevel=0>i</mi>
                <mi scriptlevel=0>n</mi>
                <mi scriptlevel=0>c</mi>
                <mo>(</mo>
                <mi scriptlevel=0>k</mi>
                <mi scriptlevel=0>d</mi>
                <mi scriptlevel=0>c</mi>
                <mi scriptlevel=0>o</mi>
                <mi scriptlevel=0>s</mi>
                <mi scriptlevel=0>α</mi>
                <mo>)</mo>
              </mrow>
            </mfrac>
          </math>`,
          `Note that the error is still less than 1 dB when using the first approximation if the angle is not known and
          <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <mi>k</mi>
            <mi>d</mi>
            <mo><</mo>
            <mn>1.15</mn>
          </math>`,
        ],
        image: `${config.ASSETS_URL}/01/case-02/slide02-image01.svg`,
      },
    },
  },
};

export default principle;
